import { type HTMLAttributes, type JSX, useState } from 'react';

type AvatarVariant = 'member' | 'grumete' | 'partner' | 'pendiente' | 'accent';

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl' | number;

type Props = HTMLAttributes<HTMLSpanElement> & {
	/** URL de un sello superpuesto abajo a la derecha (p. ej. `/brand/experiencia_oficial.png`). */
	badge?: string;
	/** Texto alternativo del sello; por defecto decorativo (`alt=''`). */
	badgeAlt?: string;
	/** Alimenta el `alt` de la imagen y, sin foto, las iniciales y el `aria-label` del contenedor. */
	name: string;
	/** Muestra anillo de color del rol. */
	ring?: boolean;
	/** Tamaño nombrado o en píxeles. sm 32 · md 44 · lg 64 · xl 120. */
	size?: AvatarSize;
	/** URL de la imagen. Opcional: sin foto se pintan las iniciales de `name`. */
	src?: string;
	/** Anillo de 3px en vez de 2px. */
	thick?: boolean;
	/**
	 * Color del anillo. Corresponde al prop `role` de `components/badges/Avatar.d.ts` del design
	 * system del cliente; se renombra a `variant` porque `role` colisiona con el atributo ARIA
	 * nativo de `HTMLAttributes` y `lint/a11y/useValidAriaRole` de Biome rechaza un literal como
	 * `'member'` en esa posición (ver también `Badge`, mismo motivo).
	 */
	variant?: AvatarVariant;
};

const TAMANOS: Record<Exclude<AvatarSize, number>, number> = { lg: 64, md: 44, sm: 32, xl: 120 };

const ANILLO_ROL: Record<AvatarVariant, string> = {
	accent: 'ring-accent',
	grumete: 'ring-grumete',
	member: 'ring-member',
	partner: 'ring-partner',
	pendiente: 'ring-pendiente',
};

/** Sello: proporción del avatar (38%, como el `.jsx` de origen) con un mínimo legible. */
const PROPORCION_SELLO = 0.38;
const TAMANO_MINIMO_SELLO = 14;

/** Tamaño de letra de las iniciales, proporcional al avatar. */
const PROPORCION_FUENTE_INICIALES = 0.4;

const obtenerIniciales = (name: string): string => {
	const [primera, segunda] = name.trim().split(/\s+/).filter(Boolean);
	if (!primera) {
		return '';
	}
	if (!segunda) {
		return primera.slice(0, 2).toUpperCase();
	}
	return `${primera.charAt(0)}${segunda.charAt(0)}`.toUpperCase();
};

/**
 * Avatar circular, contrato `components/badges/Avatar.d.ts` del design system del cliente
 * (`src`, `size`, `ring`, `role` —aquí `variant`, ver JSDoc del prop—, `thick`, `badge`). A
 * diferencia del `.d.ts` original, aquí `src` es opcional: sin foto (o si la imagen falla al
 * cargar) se pintan las iniciales de `name` —dos letras, mayúsculas— sobre `paper-2` en
 * `text-ink` con `font-display`, dentro de un elemento con `role='img'` y `aria-label={name}`
 * que hace de contenedor accesible.
 */
export const Avatar = ({
	badge,
	badgeAlt = '',
	className = '',
	name,
	ring = false,
	size = 'md',
	src,
	style,
	thick = false,
	variant = 'accent',
	...resto
}: Props): JSX.Element => {
	const [imagenFallo, setImagenFallo] = useState(false);
	const px = typeof size === 'number' ? size : TAMANOS[size];
	const mostrarImagen = Boolean(src) && !imagenFallo;
	const claseAnillo = ring ? `${thick ? 'ring-[3px]' : 'ring-[2px]'} ${ANILLO_ROL[variant]}` : '';
	const tamanoSello = Math.max(Math.round(px * PROPORCION_SELLO), TAMANO_MINIMO_SELLO);

	return (
		<span
			className={`relative inline-flex flex-shrink-0 ${className}`}
			style={{ height: px, width: px, ...style }}
			{...resto}
		>
			{mostrarImagen ? (
				<img
					alt={name}
					className={`h-full w-full rounded-pill object-cover ${claseAnillo}`}
					loading='lazy'
					onError={() => setImagenFallo(true)}
					src={src}
				/>
			) : (
				<span
					aria-label={name}
					className={`flex h-full w-full items-center justify-center rounded-pill bg-paper-2 font-display text-ink ${claseAnillo}`}
					role='img'
					style={{ fontSize: Math.round(px * PROPORCION_FUENTE_INICIALES) }}
				>
					{obtenerIniciales(name)}
				</span>
			)}
			{badge ? (
				<img
					alt={badgeAlt}
					className='absolute rounded-pill border-2 border-paper bg-paper object-contain'
					src={badge}
					style={{ bottom: -2, height: tamanoSello, right: -2, width: tamanoSello }}
				/>
			) : null}
		</span>
	);
};
